import {
    IProcountorInvoicePreviewGroup,
    IProcountorInvoicePreviewResponse,
    IProcountorInvoicePreviewRow,
    IZentuvoInvoiceSelectionRequest
} from './ZentuvoInvoicingApi';

export type SortKey = 'createdAt' | 'invoiceNumber' | 'status' | 'brokerAgent' | 'productName' | 'totalAmount';

export interface IVisibleGroup {
    group: IProcountorInvoicePreviewGroup;
    rows: IProcountorInvoicePreviewRow[];
}

export const STATUS_OPTIONS = [
    { value: 'all', label: 'All statuses' },
    { value: '0', label: 'Unknown' },
    { value: '1', label: 'Not Yet Exported' },
    { value: '2', label: 'Completed' },
    { value: '3', label: 'Exported' },
    // { value: '4', label: 'OverPaid' },
    // { value: '5', label: 'Open Orders' }
];

export const EDITABLE_STATUS_OPTIONS = STATUS_OPTIONS.filter((x) => x.value !== 'all');

export function dateToApiValue(value: Date | null): string {
    if (!value) {
        return '';
    }

    return value.toISOString().split('T')[0];
}

export interface IMasterRecordNote {
    text: string;
    isWarning: boolean;
}

// Shown instead of the header CSV line when the backend could not build one.
export function getMasterRecordNote(group: IProcountorInvoicePreviewGroup): IMasterRecordNote {
    if (group.ProcountorIdAssignedAtExport) {
        return { text: 'The 99-number comes from zPro at export.', isWarning: false };
    }

    return {
        text: group.ValidationMessage || 'Master record cannot be built until ProcountorId is fixed.',
        isWarning: true
    };
}

export function formatCurrency(value: number): string {
    return `${value.toFixed(2)} EUR`;
}

export function formatDateTime(value: string): string {
    if (!value) {
        return '';
    }

    return value.replace('T', ' ').substring(0, 16);
}

export async function copyTextToClipboard(text: string): Promise<void> {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    const input = document.createElement('textarea');
    input.value = text;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
}

export function buildProcountorIdAssignmentPhrase(agentName: string, brokerFirmName: string, procountorId: string): string {
    return `"${agentName || 'Unknown agent'}" from BO "${brokerFirmName || 'Unknown firm'}" ProcountorId is ${procountorId}`;
}

export function getErrorMessage(error: any, fallbackMessage: string): string {
    if (error && error.message) {
        return error.message;
    }

    return fallbackMessage;
}

export function normalizeOrderRowId(orderRowId: number | string): number {
    const normalized = Number(orderRowId);
    return Number.isNaN(normalized) ? -1 : normalized;
}

export function collectDefaultSelectedRowIds(response: IProcountorInvoicePreviewResponse): number[] {
    const selectedIds: number[] = [];

    (response.Groups || []).forEach((group) => {
        (group.Rows || []).forEach((row) => {
            if (row.IsDefaultSelected) {
                selectedIds.push(normalizeOrderRowId(row.OrderRowId));
            }
        });
    });

    return selectedIds;
}

export function keepExistingSelection(response: IProcountorInvoicePreviewResponse, previouslySelectedIds: number[]): number[] {
    const validIds: number[] = [];

    (response.Groups || []).forEach((group) => {
        (group.Rows || []).forEach((row) => {
            const normalizedOrderRowId = normalizeOrderRowId(row.OrderRowId);
            if (previouslySelectedIds.indexOf(normalizedOrderRowId) >= 0) {
                validIds.push(normalizedOrderRowId);
            }
        });
    });

    return validIds;
}

export function getInitialExpandedGroupIds(response: IProcountorInvoicePreviewResponse): string[] {
    return (response.Groups || [])
        .filter((group) => !group.HasValidProcountorId)
        .map((group) => group.GroupId);
}

export function getSearchText(group: IProcountorInvoicePreviewGroup, row: IProcountorInvoicePreviewRow): string {
    return [
        group.ProcountorId,
        group.InvoiceReceiver,
        group.InvoiceEmail,
        row.InvoiceNumber,
        row.OrderCollectionNumber,
        row.BrokerAgent,
        row.BrokerFirm,
        row.ProductName,
        row.InvoiceComment,
        row.AddressLine,
        row.AddressLine1,
        row.AddressCity,
        row.AddressPostalNumber,
        row.RowCsvLine
    ]
        .join(' ')
        .toLowerCase();
}

export function sortRows(rows: IProcountorInvoicePreviewRow[], sortKey: SortKey, sortDirection: 'asc' | 'desc'): IProcountorInvoicePreviewRow[] {
    const sortedRows = rows.slice();
    sortedRows.sort((leftRow, rightRow) => {
        let leftValue: string | number = '';
        let rightValue: string | number = '';

        switch (sortKey) {
            case 'invoiceNumber':
                leftValue = leftRow.InvoiceNumber;
                rightValue = rightRow.InvoiceNumber;
                break;
            case 'status':
                leftValue = leftRow.InvoiceStatusValue;
                rightValue = rightRow.InvoiceStatusValue;
                break;
            case 'brokerAgent':
                leftValue = leftRow.BrokerAgent;
                rightValue = rightRow.BrokerAgent;
                break;
            case 'productName':
                leftValue = `${leftRow.ProductName} ${leftRow.InvoiceComment}`;
                rightValue = `${rightRow.ProductName} ${rightRow.InvoiceComment}`;
                break;
            case 'totalAmount':
                leftValue = leftRow.TotalAmount;
                rightValue = rightRow.TotalAmount;
                break;
            case 'createdAt':
            default:
                leftValue = leftRow.CreatedAt;
                rightValue = rightRow.CreatedAt;
                break;
        }

        if (leftValue < rightValue) {
            return sortDirection === 'asc' ? -1 : 1;
        }

        if (leftValue > rightValue) {
            return sortDirection === 'asc' ? 1 : -1;
        }

        return 0;
    });

    return sortedRows;
}

export function buildSelectionRequest(
    fromDate: Date | null,
    toDate: Date | null,
    directInvoices: boolean,
    orderRowIds: number[]
): IZentuvoInvoiceSelectionRequest {
    return {
        From: dateToApiValue(fromDate),
        To: dateToApiValue(toDate),
        Single: directInvoices,
        OrderRowIds: orderRowIds
    };
}
